import { SyntheticEvent, useState } from 'react';

import { SelectChangeEvent } from '@mui/material';
import { get } from 'lodash';

import { ChartFilterProps } from 'entities/Charts/sdk';
import { USStatesListResponse, useUsStates } from 'entities/Dataset/sdk';

import Autocomplete from 'components/Autocomplete';
import FormControl from 'components/FormControl';
import FormLabel from 'components/FormLabel';
import MenuItem from 'components/MenuItem';
import Select from 'components/Select';
import MuiTextField from 'components/TextField';

interface MisconceptionsBarChartFiltersProps {
  filters: ChartFilterProps;
  onFilter: (filters: ChartFilterProps) => void;
}

const MisconceptionsBarChartFilters = ({
  filters,
  onFilter
}: MisconceptionsBarChartFiltersProps) => {
  const [gender, setGender] = useState<string>(filters.gender);
  const [grade, setGrade] = useState<string>(filters.grade);
  const [unit, setUnit] = useState<string>(filters.unit);
  const [ethnicity, setEthnicity] = useState<string>(filters.ethnicity);
  const [state, setState] = useState<string>(filters.state);
  const [isLowIncome, setIsLowIncome] = useState<string>(filters.is_low_income);
  const [isOnIEP, setIsOnIEP] = useState<string>(
    filters.is_on_individualized_education_plan
  );
  const [englishLearnerStatus, setEnglishLearnerStatus] = useState<string>(
    filters.english_learner_status
  );

  const [standardCode, setStandardCode] = useState<string>(
    filters.standard_code
  );

  const { data: usStates } = useUsStates();

  const onGenderChange = (event: SelectChangeEvent) => {
    const newGender = event.target.value as string;
    setGender(newGender);
    onFilter({
      gender: newGender,
      grade,
      unit,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      english_learner_status: englishLearnerStatus,
      standard_code: standardCode
    });
  };

  const onGradeChange = (event: SelectChangeEvent) => {
    const newGrade = event.target.value as string;
    setGrade(newGrade);
    onFilter({
      gender,
      grade: newGrade,
      unit,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      english_learner_status: englishLearnerStatus,
      standard_code: standardCode
    });
  };

  const onUnitChange = (event: SelectChangeEvent) => {
    const newUnit = event.target.value as string;
    setUnit(newUnit);
    onFilter({
      gender,
      grade,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      english_learner_status: englishLearnerStatus,
      unit: newUnit,
      standard_code: standardCode
    });
  };

  const onRaceChange = (event: SelectChangeEvent) => {
    const newRace = event.target.value as string;
    setEthnicity(newRace);
    onFilter({
      gender,
      grade,
      unit,
      ethnicity: newRace,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      english_learner_status: englishLearnerStatus,
      standard_code: standardCode
    });
  };

  const onStateChange = (
    _event: SyntheticEvent,
    value: USStatesListResponse | null
  ) => {
    const newState = get(value, 'abbreviation', '');
    setState(newState);
    onFilter({
      gender,
      grade,
      unit,
      ethnicity,
      state: newState,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      english_learner_status: englishLearnerStatus,
      standard_code: standardCode
    });
  };

  const onIsLowIncomeChange = (event: SelectChangeEvent) => {
    const newIsLowIncome = event.target.value as string;
    setIsLowIncome(newIsLowIncome);
    onFilter({
      gender,
      grade,
      unit,
      ethnicity,
      state,
      is_low_income: newIsLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      english_learner_status: englishLearnerStatus,
      standard_code: standardCode
    });
  };

  const onIsOnIEPChange = (event: SelectChangeEvent) => {
    const newIsOnIEP = event.target.value as string;
    setIsOnIEP(newIsOnIEP);
    onFilter({
      gender,
      grade,
      unit,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: newIsOnIEP,
      english_learner_status: englishLearnerStatus,
      standard_code: standardCode
    });
  };

  const onEnglishLearnerStatusChange = (event: SelectChangeEvent) => {
    const newEnglishLearnerStatus = event.target.value as string;
    setEnglishLearnerStatus(newEnglishLearnerStatus);
    onFilter({
      gender,
      grade,
      unit,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      english_learner_status: newEnglishLearnerStatus,
      standard_code: standardCode
    });
  };

  const onStandardCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newStandardCode = event.target.value;
    setStandardCode(newStandardCode);

    onFilter({
      gender,
      grade,
      ethnicity,
      state,
      is_low_income: isLowIncome,
      is_on_individualized_education_plan: isOnIEP,
      english_learner_status: englishLearnerStatus,
      unit,
      standard_code: newStandardCode
    });
  };

  return (
    <div className="flex flex-1 flex-wrap items-center justify-between gap-5">
      <div className="flex w-full flex-wrap items-center gap-5">
        <FormControl className="w-64">
          <FormLabel id="state-label">State</FormLabel>

          <Autocomplete
            className="bg-white"
            disablePortal
            options={usStates ?? []}
            renderInput={(params) => <MuiTextField {...params} />}
            getOptionLabel={(state) => `${state.name} (${state.abbreviation})`}
            value={usStates?.find((s) => s.abbreviation === state) ?? null}
            onChange={onStateChange}
          />
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="standard-code-label">Standard</FormLabel>
          <MuiTextField
            className="bg-white"
            id="standard-code-label"
            value={standardCode}
            onChange={onStandardCodeChange}
            placeholder="CCSS.MATH.CONTENT.6.NS.B.2"
          />
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="grade-label">Grade</FormLabel>

          <Select
            className="bg-white"
            labelId="grade-label"
            value={grade}
            onChange={onGradeChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">1</MenuItem>
            <MenuItem value="2">2</MenuItem>
            <MenuItem value="3">3</MenuItem>
            <MenuItem value="4">4</MenuItem>
            <MenuItem value="5">5</MenuItem>
            <MenuItem value="6">6</MenuItem>
            <MenuItem value="7">7</MenuItem>
            <MenuItem value="8">8</MenuItem>
            <MenuItem value="9">9</MenuItem>
            <MenuItem value="10">10</MenuItem>
            <MenuItem value="11">11</MenuItem>
            <MenuItem value="12">12</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="unit-label">Unit</FormLabel>

          <Select
            className="bg-white"
            labelId="unit-label"
            value={unit}
            onChange={onUnitChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="4">1</MenuItem>
            <MenuItem value="5">2</MenuItem>
            <MenuItem value="6">3</MenuItem>
            <MenuItem value="7">4</MenuItem>
            <MenuItem value="8">5</MenuItem>
            <MenuItem value="9">6</MenuItem>
            <MenuItem value="10">7</MenuItem>
            <MenuItem value="11">8</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="gender-label">Gender</FormLabel>

          <Select
            className="bg-white"
            labelId="gender-label"
            value={gender}
            onChange={onGenderChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="M">Male</MenuItem>
            <MenuItem value="F">Female</MenuItem>
            <MenuItem value="U">Unknown</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="ethnicity-label">Ethnicity</FormLabel>

          <Select
            className="bg-white"
            labelId="ethnicity-label"
            value={ethnicity}
            onChange={onRaceChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="asian">Asian</MenuItem>
            <MenuItem value="white">White</MenuItem>
            <MenuItem value="hispanic">Hispanic/Latino</MenuItem>
            <MenuItem value="black">Black</MenuItem>
            <MenuItem value="native_american">Native American</MenuItem>
            <MenuItem value="two_or_more">Two or more races</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="low-income-label">Is low income</FormLabel>

          <Select
            className="bg-white"
            labelId="low-income-label"
            value={isLowIncome}
            onChange={onIsLowIncomeChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="iep-label">IEP</FormLabel>

          <Select
            className="bg-white"
            labelId="iep-label"
            value={isOnIEP}
            onChange={onIsOnIEPChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>

        <FormControl className="w-64">
          <FormLabel id="lep-label">LEP</FormLabel>

          <Select
            className="bg-white"
            labelId="lep-label"
            value={englishLearnerStatus}
            onChange={onEnglishLearnerStatusChange}
            displayEmpty
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="partime bl, should be fulltime bl">
              partime bl, should be fulltime bl
            </MenuItem>
            <MenuItem value="special ed">special ed</MenuItem>
            <MenuItem value="waiver">waiver</MenuItem>
            <MenuItem value="part-time biling">part-time biling</MenuItem>
            <MenuItem value="dual lang">dual lang</MenuItem>
            <MenuItem value="tpi student">tpi student</MenuItem>
            <MenuItem value="tbe student">tbe student</MenuItem>
            <MenuItem value="lep">lep</MenuItem>
            <MenuItem value="not lep">not lep</MenuItem>
            <MenuItem value="exited program">exited program</MenuItem>
            <MenuItem value="no access, remained dl">
              no access, remained dl
            </MenuItem>
            <MenuItem value="bp">bp</MenuItem>
            <MenuItem value="bpb">bpb</MenuItem>
            <MenuItem value="dl">dl</MenuItem>
            <MenuItem value="i">i</MenuItem>
            <MenuItem value="sp">sp</MenuItem>
            <MenuItem value="w">w</MenuItem>
            <MenuItem value="not eligible">not eligible</MenuItem>
          </Select>
        </FormControl>
      </div>
    </div>
  );
};

export default MisconceptionsBarChartFilters;
